/**
 * 読み(ひらがな列)から受理オートマトンを構築する。
 *
 * ## モデル
 * ノード = 読みのかなインデックス(0 〜 kanaLength)。
 * エッジ = 「そのかな(または2かな)を確定させるローマ字文字列」。
 * 受理される打鍵列 = ノード0からノードkanaLengthまでの経路上の文字列の連結。
 *
 * 文脈依存の2つは**エッジ結合**で表現する:
 *
 * - 促音「っ」: 後続エッジ `r` の先頭が重ね可能な子音 `c` なら `c + r` というエッジを
 *   「っ」の位置から後続エッジの終端へ張る。表記の一貫性(`ssi` は許すが `sci` は許さない)が
 *   自動的に保証され、`っっか` = `kkka` のような連鎖も後ろから構築するだけで成立する。
 * - 撥音「ん」: 後続エッジ `r` の先頭が母音でも `n` でも `y` でもない子音なら `n + r` を張る。
 *   それ以外(語末・母音前・な行前・や行前)は `nn` / `xn` だけが残るので **nn必須**になる。
 *   `n` を1つ打った時点では `nn` エッジと `n+r` エッジの両方が生きているため、
 *   次に `n` を打てば `nn` に、子音を打てば単独 `n` に自然に確定する
 *   (Unity版 TypingManager の「n単独→次入力でnn化」挙動の一般化)。
 *
 * 表示用の代表表記は「現在生きている経路のうち最短のもの」を選ぶため、
 * `si` と打ってから `h` を打つと `shi` に、`s` の時点では `si` に追随する。
 */

import {
  HATSUON,
  HATSUON_SPELLINGS,
  MAX_KANA_UNIT_LENGTH,
  SOKUON,
  SOKUON_SPELLINGS,
  getSpellings,
  isDoublableConsonant,
  isHatsuonTerminator,
  normalizeReading,
} from './table.js';

/** オートマトンの1エッジ。 */
export interface RomajiEdge {
  /** 開始かなインデックス。 */
  readonly from: number;
  /** 終了かなインデックス。 */
  readonly to: number;
  /** 打鍵すべき文字列。 */
  readonly text: string;
}

interface ActiveEdge {
  readonly edge: RomajiEdge;
  /** すでに消費した文字数(1 以上、text.length 未満)。 */
  readonly pos: number;
}

/**
 * `noUncheckedIndexedAccess` 対策。
 * 添字は構築ロジック上つねに有効なので、undefined は到達不能なバグを意味する。
 */
function must<T>(value: T | undefined): T {
  /* v8 ignore next */
  if (value === undefined) throw new RangeError('romaji automaton: unreachable index');
  return value;
}

/**
 * 読み1語ぶんの受理オートマトン。イミュータブルで、複数のカーソルから共有できる。
 */
export class RomajiAutomaton {
  /** 正規化前の読み。 */
  readonly source: string;
  /** 正規化後の読み(判定に使う)。 */
  readonly reading: string;
  /** 読みのかな数。 */
  readonly kanaLength: number;

  private readonly edgesFrom: readonly (readonly RomajiEdge[])[];
  /** best[i] = ノード i から終端までの最短ローマ字列。 */
  private readonly best: readonly string[];

  constructor(reading: string) {
    this.source = reading;
    this.reading = normalizeReading(reading);
    const chars = [...this.reading];
    this.kanaLength = chars.length;

    const edgesFrom: RomajiEdge[][] = Array.from({ length: this.kanaLength + 1 }, () => []);
    // 後ろから構築する。促音・撥音は「後続エッジ」を参照するため、この順序が必須。
    for (let i = this.kanaLength - 1; i >= 0; i--) {
      const bucket = must(edgesFrom[i]);
      const nextBucket = must(edgesFrom[i + 1]);
      const ch = must(chars[i]);

      // 拗音などの複数かなユニット(長い方を優先して先に積む)。
      for (let len = MAX_KANA_UNIT_LENGTH; len >= 2; len--) {
        if (i + len > this.kanaLength) continue;
        const unit = chars.slice(i, i + len).join('');
        const spellings = getSpellings(unit);
        if (!spellings) continue;
        for (const text of spellings) bucket.push({ from: i, to: i + len, text });
      }

      if (ch === SOKUON) {
        for (const text of SOKUON_SPELLINGS) bucket.push({ from: i, to: i + 1, text });
        for (const next of nextBucket) {
          const head = next.text.charAt(0);
          if (isDoublableConsonant(head)) {
            bucket.push({ from: i, to: next.to, text: head + next.text });
          }
        }
      } else if (ch === HATSUON) {
        for (const text of HATSUON_SPELLINGS) bucket.push({ from: i, to: i + 1, text });
        for (const next of nextBucket) {
          if (isHatsuonTerminator(next.text.charAt(0))) {
            bucket.push({ from: i, to: next.to, text: 'n' + next.text });
          }
        }
      } else {
        const spellings = getSpellings(ch);
        if (spellings) {
          for (const text of spellings) bucket.push({ from: i, to: i + 1, text });
        } else {
          // テーブルに無い文字(ASCII英数・記号・スペースなど)はそのままキーとして扱う。
          bucket.push({ from: i, to: i + 1, text: ch.toLowerCase() });
        }
      }
    }
    this.edgesFrom = edgesFrom;

    const best: string[] = new Array<string>(this.kanaLength + 1).fill('');
    for (let i = this.kanaLength - 1; i >= 0; i--) {
      let chosen = '';
      let shortest = Number.POSITIVE_INFINITY;
      for (const edge of must(edgesFrom[i])) {
        const candidate = edge.text + must(best[edge.to]);
        if (candidate.length < shortest) {
          shortest = candidate.length;
          chosen = candidate;
        }
      }
      best[i] = chosen;
    }
    this.best = best;
  }

  /** 何も打っていない状態での代表(最短)ローマ字列。 */
  get romaji(): string {
    return this.shortestFrom(0);
  }

  /** ノード `index` から出るエッジ一覧(デバッグ・テスト用)。 */
  edgesAt(index: number): readonly RomajiEdge[] {
    return this.edgesFrom[index] ?? [];
  }

  /** ノード `index` から終端までの最短ローマ字列。 */
  shortestFrom(index: number): string {
    return this.best[index] ?? '';
  }

  /** 新しい入力カーソルを作る。 */
  createCursor(): RomajiCursor {
    return new RomajiCursor(this);
  }
}

/**
 * 1語ぶんの入力状態(可変)。`RomajiAutomaton` を共有して複数作れる。
 */
export class RomajiCursor {
  private nodes: number[] = [0];
  private actives: ActiveEdge[] = [];
  private keys = '';

  constructor(readonly automaton: RomajiAutomaton) {}

  /** 入力状態を初期化する。 */
  reset(): void {
    this.nodes = [0];
    this.actives = [];
    this.keys = '';
  }

  /** 語が完成したか。 */
  get completed(): boolean {
    return this.nodes.includes(this.automaton.kanaLength);
  }

  /** これまでに受理した打鍵列。 */
  get typed(): string {
    return this.keys;
  }

  /** 現在の状態から最短で語を完成させる残りのローマ字列。 */
  get remaining(): string {
    if (this.completed) return '';
    const candidates = [
      ...this.nodes.map((node) => this.automaton.shortestFrom(node)),
      ...this.actives.map(
        (active) =>
          active.edge.text.slice(active.pos) + this.automaton.shortestFrom(active.edge.to),
      ),
    ];
    let chosen = '';
    let shortest = Number.POSITIVE_INFINITY;
    for (const candidate of candidates) {
      if (candidate.length < shortest) {
        shortest = candidate.length;
        chosen = candidate;
      }
    }
    return chosen;
  }

  /** 入力済みと整合する代表ローマ字列(= typed + remaining)。ガイド表示に使う。 */
  get guide(): string {
    return this.keys + this.remaining;
  }

  /** 次に受理されるキーの集合(昇順・重複なし)。 */
  get nextKeys(): readonly string[] {
    if (this.completed) return [];
    const set = new Set<string>();
    for (const node of this.nodes) {
      for (const edge of this.automaton.edgesAt(node)) set.add(edge.text.charAt(0));
    }
    for (const active of this.actives) set.add(active.edge.text.charAt(active.pos));
    return [...set].sort();
  }

  /**
   * 確定済みのかな数(下限)。
   * 生きている全経路が到達済みのかなインデックスを返すので、ルビ強調などに使える。
   */
  get kanaIndex(): number {
    let min = this.automaton.kanaLength;
    for (const node of this.nodes) min = Math.min(min, node);
    for (const active of this.actives) min = Math.min(min, active.edge.from);
    return min;
  }

  /**
   * 1打鍵を入力する。受理できたら `true`。
   * 受理できなかった場合、状態は変化しない(ミス扱いは呼び出し側の責務)。
   */
  input(key: string): boolean {
    if (key.length !== 1) return false;
    if (this.completed) return false;
    const ch = key.toLowerCase();

    const nextNodes = new Set<number>();
    const nextActives: ActiveEdge[] = [];

    for (const node of this.nodes) {
      for (const edge of this.automaton.edgesAt(node)) {
        if (edge.text.charAt(0) !== ch) continue;
        if (edge.text.length === 1) nextNodes.add(edge.to);
        else nextActives.push({ edge, pos: 1 });
      }
    }
    for (const active of this.actives) {
      if (active.edge.text.charAt(active.pos) !== ch) continue;
      const pos = active.pos + 1;
      if (pos === active.edge.text.length) nextNodes.add(active.edge.to);
      else nextActives.push({ edge: active.edge, pos });
    }

    if (nextNodes.size === 0 && nextActives.length === 0) return false;

    this.nodes = [...nextNodes];
    this.actives = nextActives;
    this.keys += ch;
    return true;
  }
}

/** 読みの代表(最短)ローマ字表記を返す。コンテンツ検証やテストに便利。 */
export function toRomaji(reading: string): string {
  return new RomajiAutomaton(reading).romaji;
}

/**
 * 打鍵列がその読みとして受理されるかを判定する(テスト・検証用)。
 */
export function acceptsRomaji(reading: string, keys: string): boolean {
  const cursor = new RomajiAutomaton(reading).createCursor();
  for (const key of keys) {
    if (!cursor.input(key)) return false;
  }
  return cursor.completed;
}
